/**
 * CostSummaryCard Component
 *
 * Displays a summary metric card with title, value, and optional subtitle.
 * Used for displaying cost totals and key metrics on the dashboard.
 *
 * Part of US-P7-001 Step 5: Cost Summary Dashboard
 */

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Format a currency value in British pounds
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * CostSummaryCard Component
 *
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {number|null} props.value - Value to display
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function CostSummaryCard({
  title,
  value,
  subtitle,
  isLoading = false,
  className = ''
}) {
  return (
    <Card className={`${className}`}>
      <CardContent className="pt-6">
        <div className="space-y-2">
          {/* Title */}
          <p className="text-sm font-medium text-neutral-600">{title}</p>

          {/* Value */}
          {isLoading ? (
            <Skeleton className="h-10 w-32" />
          ) : (
            <p className="text-3xl font-bold text-neutral-900">
              {formatCurrency(value)}
            </p>
          )}

          {/* Subtitle */}
          {subtitle && (
            <p className="text-xs text-neutral-500">{subtitle}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * CostSummaryCardSkeleton - Loading state for CostSummaryCard
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 */
export function CostSummaryCardSkeleton({ className = '' }) {
  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  )
}
